import React, { memo, useCallback, useEffect, useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import { DateFieldCDS, TextFieldCDS } from '@sharedCR/coreDesignSystem/form';
import ModalFormWrapper from 'app/shared/componentsRestyled/coreDesignSystem/ModalFormWrapper';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import { MAX_CALENDAR_DATE, MIN_CALENDAR_DATE } from 'constants/date';
import useForm from 'hooks/useForm';

import {
  SALE_ID_MAX_LENGTH,
  TRANSFER_CONFIRMATION_NOTE,
} from './MersTransferDetailsForm.constants';
import { MersTransferDetailsFormTestIds } from './MersTransferDetailsForm.testIds';
import {
  buildMersTransferDetails,
  getMersTransferDetailsSchema,
  getTransferDateFields,
} from './MersTransferDetailsForm.utils';

const MersTransferDetailsForm = ({
  kind,
  flow = false,
  isLoading,
  setIsLoading,
  formRef,
  onSubmit,
}) => {
  const {
    form,
    setSchema,
    setInForm,
    validateForm,
    validateField,
    validationErrors,
    handleChange,
  } = useForm({});

  const dateFields = useMemo(() => getTransferDateFields({ kind, flow }), [kind, flow]);

  const onDateFieldChange = useCallback(
    (value, name) => {
      setInForm(name, value);
      validateField(name, value);
    },
    [setInForm, validateField],
  );

  const onSubmitForm = useCallback(
    async e => {
      e?.preventDefault?.();

      if (!(await validateForm({ noFormChangedValidate: true }))) {
        return;
      }

      setIsLoading(true);
      onSubmit(buildMersTransferDetails(form, { kind, flow })).finally(() => setIsLoading(false));
    },
    [validateForm, form, kind, flow, onSubmit, setIsLoading],
  );

  useEffect(() => {
    setSchema(getMersTransferDetailsSchema({ kind, flow }));
  }, [setSchema, kind, flow]);

  return (
    <ModalFormWrapper
      isFormChanged
      formRef={formRef}
      formTestId={MersTransferDetailsFormTestIds.FORM}
      isSubmitting={isLoading}
      submitButtonName="Send to MERS"
      submitButtonTestId={MersTransferDetailsFormTestIds.SUBMIT_BUTTON}
      onSubmit={onSubmitForm}
    >
      <Typography className="mb-16" color="text.secondary" component="p" variant="regular14">
        {TRANSFER_CONFIRMATION_NOTE}
      </Typography>
      <Box className="flex flex-col gap-16">
        {dateFields.map(field => (
          <DateFieldCDS
            dataTestId={field.dataTestId}
            disabled={isLoading}
            error={!!validationErrors[field.name]}
            helperText={validationErrors[field.name]}
            isHidden={field.isHidden}
            key={field.name}
            label={field.label}
            maxDate={field.maxDate ?? MAX_CALENDAR_DATE}
            minDate={field.minDate ?? MIN_CALENDAR_DATE}
            name={field.name}
            required={field.isRequired}
            value={form[field.name] || null}
            onChange={onDateFieldChange}
          />
        ))}
        <TextFieldCDS
          dataTestId={MersTransferDetailsFormTestIds.SALE_ID}
          disabled={isLoading}
          error={!!validationErrors.saleId}
          helperText={validationErrors.saleId}
          label="Sale ID"
          maxLength={SALE_ID_MAX_LENGTH}
          name="saleId"
          value={form.saleId ?? ''}
          onChange={handleChange}
        />
      </Box>
    </ModalFormWrapper>
  );
};

MersTransferDetailsForm.propTypes = {
  ...WithLoadingPropTypes,
  kind: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  flow: PropTypes.bool,
};

export default compose(withLoading, memo)(MersTransferDetailsForm);
